import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Search, Filter, Trash2, Wallet, Settings, Download } from "lucide-react";
import { toast } from "sonner";
import { formatCLP, estadoInfo, ESTADOS_RENDICION, ESTADOS_EDITABLES } from "@/lib/gastos";

export default function RendicionGastos() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const isAdmin = user?.cargo === "admin";
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, borradores: 0, completadas: 0 });
  const [loading, setLoading] = useState(true);
  const [sinCategorias, setSinCategorias] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ q: "", estado: "", fecha_desde: "", fecha_hasta: "" });

  const buildParams = (f) => {
    const p = new URLSearchParams();
    if (f.q) p.set("q", f.q);
    if (f.estado && f.estado !== "todos") p.set("estado", f.estado);
    if (f.fecha_desde) p.set("fecha_desde", f.fecha_desde);
    if (f.fecha_hasta) p.set("fecha_hasta", f.fecha_hasta);
    return p.toString();
  };

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const [r, s, c] = await Promise.all([
        api.get(`/api/gastos/rendiciones?${buildParams(f)}`),
        api.get("/api/gastos/stats").catch(() => ({ data: {} })),
        api.get("/api/gastos/categorias").catch(() => ({ data: [] })),
      ]);
      setRows(r.data || []);
      setStats(s.data || {});
      setSinCategorias((c.data || []).length === 0);
    } catch {
      toast.error("Error al cargar rendiciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const uf = (f, v) => setFilters((p) => ({ ...p, [f]: v }));
  const clearFilters = () => {
    const empty = { q: "", estado: "", fecha_desde: "", fecha_hasta: "" };
    setFilters(empty);
    load(empty);
  };

  const exportExcel = async () => {
    try {
      const r = await api.get(`/api/gastos/export?${buildParams(filters)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rendiciones_gastos_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al exportar"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar la rendición ${r.folio}?`)) return;
    try { await api.delete(`/api/gastos/rendiciones/${r.id}`); toast.success("Rendición eliminada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Wallet className="h-5 w-5 text-[#534AB7] flex-shrink-0" />
            <span className="text-sm font-semibold text-[#384091] truncate">Rendición de Gastos</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Filtros</span>
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={exportExcel} title="Exportar a Excel">
                <Download className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Excel</span>
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => navigate("/tecnico/gastos/config")}>
                <Settings className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Configuración</span>
              </Button>
            )}
            <Button size="sm" className="bg-[#534AB7] hover:bg-[#443C9E] text-white"
              onClick={() => navigate("/tecnico/gastos/nueva")}>
              <Plus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Nueva</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {sinCategorias && isAdmin && (
          <Card className="border-0 shadow-sm bg-amber-50">
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <p className="text-sm text-amber-800">
                Aún no hay categorías de gasto configuradas.
              </p>
              <Button size="sm" variant="outline" className="border-amber-500 text-amber-700 flex-shrink-0"
                onClick={() => navigate("/tecnico/gastos/config")}>
                Configurar
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Borradores", value: (stats.borradores || 0) + (stats.observadas || 0), color: "#D97706" },
            { label: "Enviadas", value: stats.enviadas || 0, color: "#2563EB" },
            { label: "Aprobadas", value: stats.aprobadas || 0, color: "#059669" },
            { label: "Reembolsadas", value: stats.reembolsadas || 0, color: "#0D999C" },
          ].map((s) => (
            <Card key={s.label} className="border-0 shadow-sm">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {showFilters && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div><Label className="text-xs">Buscar</Label><Input value={filters.q} onChange={(e) => uf("q", e.target.value)} className="h-11 sm:h-9 text-xs" placeholder="Folio, título, categoría..." autoComplete="off" /></div>
                <div><Label className="text-xs">Estado</Label>
                  <Select value={filters.estado} onValueChange={(v) => uf("estado", v)}>
                    <SelectTrigger className="h-11 sm:h-9 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {Object.entries(ESTADOS_RENDICION).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Fecha desde</Label><Input type="date" value={filters.fecha_desde} onChange={(e) => uf("fecha_desde", e.target.value)} className="h-11 sm:h-9 text-xs" /></div>
                <div><Label className="text-xs">Fecha hasta</Label><Input type="date" value={filters.fecha_hasta} onChange={(e) => uf("fecha_hasta", e.target.value)} className="h-11 sm:h-9 text-xs" /></div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button size="sm" className="h-11 sm:h-9 bg-[#534AB7] text-white" onClick={load}><Search className="h-3.5 w-3.5 mr-1" /> Buscar</Button>
                <Button variant="outline" size="sm" className="h-11 sm:h-9" onClick={clearFilters}>Limpiar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <p className="text-center text-gray-400 py-12">Cargando...</p>
        ) : rows.length === 0 ? (
          <p className="text-center text-gray-400 py-12">Sin rendiciones de gastos</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <Card key={r.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <Link to={`/tecnico/gastos/${r.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <Wallet className="h-4 w-4 text-[#534AB7] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {r.folio}{r.titulo ? ` — ${r.titulo}` : ""}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {r.fecha_rendicion} · {(r.items || []).length} gasto(s) · {r.tecnico_nombre || ""}
                      </p>
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                    <span className="text-sm font-semibold text-[#384091]">{formatCLP(r.total)}</span>
                    <Badge className={`${estadoInfo(r.estado).badge} border-0`}>
                      {estadoInfo(r.estado).label}
                    </Badge>
                    {(isAdmin || ESTADOS_EDITABLES.includes(r.estado)) && (
                      <Button variant="ghost" size="sm" onClick={() => remove(r)} title="Eliminar"
                        className="text-red-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
